// PositionsTab — Positions & holdings with live P&L
import React, { useState } from 'react';
import { useTrading } from '../../../context/TradingContext';
import { usePositionTracker } from '../../../hooks/usePositionTracker';
import PnLBadge from '../shared/PnLBadge';
import PositionCard from '../shared/PositionCard';
import HoldingCard from '../shared/HoldingCard';

const SUB_TABS = ['Positions', 'Holdings'];

export default function PositionsTab() {
  const { connectionStatus, setOrderModal } = useTrading();
  const { positions, holdings, positionSummary, holdingSummary, isLive } = usePositionTracker();
  const [subTab, setSubTab] = useState(0);

  if (connectionStatus !== 'connected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
        <p className="text-sm text-gray-400">Connect to Angel One to view positions</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            {subTab === 0 ? 'Day P&L' : 'Total P&L'}
          </p>
          <PnLBadge
            value={subTab === 0 ? positionSummary.totalPnL : holdingSummary.totalPnL}
            size="lg"
          />
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            {subTab === 0 ? 'Open Positions' : 'Holdings'}
          </p>
          <p className="text-xl font-bold text-gray-900">
            {subTab === 0 ? positionSummary.openCount : holdingSummary.count}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            {subTab === 0 ? 'Invested' : 'Current Value'}
          </p>
          <p className="text-xl font-bold text-gray-900 font-mono">
            {(subTab === 0 ? positionSummary.totalInvested : holdingSummary.totalCurrent)
              .toLocaleString('en-IN', { maximumFractionDigits: 0, style: 'currency', currency: 'INR' })}
          </p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-2">
        {SUB_TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setSubTab(i)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
              subTab === i
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab}
          </button>
        ))}
        {isLive && (
          <div className="ml-auto flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse" />
            <span className="text-[10px] text-gray-400">Live</span>
          </div>
        )}
      </div>

      {/* Positions */}
      {subTab === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 bg-gray-50">
            <div className="col-span-2">Symbol</div>
            <div className="col-span-1">Product</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">Avg Price</div>
            <div className="col-span-2 text-right">LTP</div>
            <div className="col-span-2 text-right">P&L</div>
            <div className="col-span-2 text-center">Actions</div>
          </div>
          {positions.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400">No open positions</div>
          ) : (
            positions.map((pos, i) => (
              <PositionCard key={i} position={pos} onTrade={setOrderModal} />
            ))
          )}
        </div>
      )}

      {/* Holdings */}
      {subTab === 1 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 bg-gray-50">
            <div className="col-span-2">Symbol</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">Avg Price</div>
            <div className="col-span-2 text-right">LTP</div>
            <div className="col-span-2 text-right">Invested</div>
            <div className="col-span-2 text-right">P&L</div>
            <div className="col-span-1 text-center">Action</div>
          </div>
          {holdings.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400">No holdings</div>
          ) : (
            holdings.map((hold, i) => (
              <HoldingCard key={i} holding={hold} onTrade={setOrderModal} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
